import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Loader2,
  Trophy,
  Info,
} from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ResponsiveRadar } from '@nivo/radar';
import { advancedReportsAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { cn } from '../../lib/utils';

interface QualityScore {
  conversation_id: number;
  contact_name: string;
  agent_name: string;
  overall_score: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  detailed_scores: {
    completeness: number;
    tone: number;
    resolution: number;
    guidelines: number;
    timeliness: number;
  };
  date: string;
}

interface QualityMetrics {
  average_score: number;
  grade_distribution: {
    A: number;
    B: number;
    C: number;
    D: number;
    F: number;
  };
  conversations: QualityScore[];
  top_performers: Array<{
    agent_name: string;
    avg_score: number;
    conversations_reviewed: number;
  }>;
  improvement_areas: Array<{
    criteria: string;
    avg_score: number;
    priority: 'High' | 'Medium' | 'Low';
  }>;
}

interface ConversationQualityScoringProps {
  startDate: string;
  endDate: string;
}

// Semantic grade colors — avoids the limited secondary/outline/destructive palette
const GRADE_CLASS: Record<string, string> = {
  A: 'bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800',
  B: 'bg-teal-100 text-teal-800 border border-teal-200 dark:bg-teal-950/50 dark:text-teal-300 dark:border-teal-800',
  C: 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800',
  D: 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-950/50 dark:text-orange-300 dark:border-orange-800',
  F: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800',
};

const PRIORITY_CONFIG = {
  High:   { Icon: AlertCircle,   dot: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',     badge: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800' },
  Medium: { Icon: AlertTriangle, dot: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800' },
  Low:    { Icon: CheckCircle2,  dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800' },
} as const;

const RANK_CONFIG = [
  { label: <Trophy className="h-3.5 w-3.5" />,  cls: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800' },
  { label: <span>#2</span>, cls: 'text-slate-500 bg-slate-100 border-slate-200 dark:bg-slate-900 dark:border-slate-700' },
  { label: <span>#3</span>, cls: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-950/40 dark:border-orange-800' },
];

const ConversationQualityScoring: React.FC<ConversationQualityScoringProps> = ({
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<QualityMetrics | null>(null);

  useEffect(() => {
    fetchQualityData();
  }, [startDate, endDate]);

  const fetchQualityData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await advancedReportsAPI.getConversationQuality({
        start_date: startDate,
        end_date: endDate,
      });
      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch quality data:', err);
      setError(err.message || 'Failed to load quality scoring data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert className="m-2">
        <AlertDescription>No quality scoring data available.</AlertDescription>
      </Alert>
    );
  }

  // Chart configs — untouched
  const gradeChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: ['A', 'B', 'C', 'D', 'F'],
    colors: ['#4caf50', '#8bc34a', '#ff9800', '#ff5722', '#f44336'],
    legend: { position: 'bottom' },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => String(data.conversations.length),
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`,
    },
  };

  const radarData = [
    {
      criteria: 'Completeness',
      score:
        data.conversations.reduce((sum, c) => sum + c.detailed_scores.completeness, 0) /
        data.conversations.length,
    },
    {
      criteria: 'Tone',
      score:
        data.conversations.reduce((sum, c) => sum + c.detailed_scores.tone, 0) /
        data.conversations.length,
    },
    {
      criteria: 'Resolution',
      score:
        data.conversations.reduce((sum, c) => sum + c.detailed_scores.resolution, 0) /
        data.conversations.length,
    },
    {
      criteria: 'Guidelines',
      score:
        data.conversations.reduce((sum, c) => sum + c.detailed_scores.guidelines, 0) /
        data.conversations.length,
    },
    {
      criteria: 'Timeliness',
      score:
        data.conversations.reduce((sum, c) => sum + c.detailed_scores.timeliness, 0) /
        data.conversations.length,
    },
  ];

  // Derived values for KPI strip
  const total         = data.conversations.length || 1;
  const excellentCt   = data.grade_distribution.A + data.grade_distribution.B;
  const poorCt        = data.grade_distribution.D + data.grade_distribution.F;
  const excellentPct  = ((excellentCt / total) * 100).toFixed(0);
  const poorPct       = ((poorCt      / total) * 100).toFixed(0);
  const scoreColor    =
    data.average_score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
    data.average_score >= 60 ? 'text-amber-600 dark:text-amber-400'    :
                               'text-red-600 dark:text-red-400';

  return (
    <div className="space-y-5">

      {/* ── AI banner — compact info strip ──────────────────────────────────── */}
      <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3 dark:border-blue-800 dark:bg-blue-950/20">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400" />
        <div>
          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
            AI-Powered Quality Analysis Coming Soon
          </p>
          <p className="mt-0.5 text-xs text-blue-700/80 dark:text-blue-300/70">
            Currently scoring on resolution status, CSAT, and response times. Next release adds AI
            sentiment, intent detection, and compliance checks.
          </p>
        </div>
      </div>

      {/* ── KPI stat strip ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 bg-muted/10 overflow-hidden">

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" />Avg Quality Score
          </span>
          <span className={cn('text-lg font-semibold tabular-nums mt-1', scoreColor)}>
            {data.average_score.toFixed(1)}
            <span className="text-sm font-normal text-muted-foreground ml-0.5">/ 100</span>
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" />Reviewed
          </span>
          <span className="text-lg font-semibold tabular-nums mt-1">
            {data.conversations.length.toLocaleString()}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[130px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" />Excellent (A + B)
          </span>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
              {excellentCt}
            </span>
            <span className="text-xs text-muted-foreground">{excellentPct}%</span>
          </div>
        </div>

        <div className="flex flex-col gap-0.5 px-4 py-3 flex-1 min-w-[150px]">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5" />Needs Improvement
          </span>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className={cn(
              'text-lg font-semibold tabular-nums',
              poorCt > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground',
            )}>
              {poorCt}
            </span>
            <span className="text-xs text-muted-foreground">{poorPct}%</span>
          </div>
        </div>

      </div>

      {/* ── Radar + Grade Distribution ─────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">

        {/* Quality Criteria Radar */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Quality Criteria Analysis</span>
            <span className="text-xs text-muted-foreground ml-1">— avg scores per dimension</span>
          </div>
          <div className="h-[320px] p-2">
            <ResponsiveRadar
              data={radarData}
              keys={['score']}
              indexBy="criteria"
              maxValue={100}
              margin={{ top: 60, right: 80, bottom: 40, left: 80 }}
              borderColor={{ from: 'color' }}
              gridLabelOffset={20}
              dotSize={10}
              dotColor={{ theme: 'background' }}
              dotBorderWidth={2}
              colors={{ scheme: 'nivo' }}
              blendMode="multiply"
              motionConfig="wobbly"
              fillOpacity={0.25}
            />
          </div>
        </div>

        {/* Grade Distribution Donut */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">Grade Distribution</span>
            <span className="text-xs text-muted-foreground ml-1">— all reviewed conversations</span>
          </div>
          <div className="p-2">
            <Chart
              options={gradeChartOptions}
              series={[
                data.grade_distribution.A,
                data.grade_distribution.B,
                data.grade_distribution.C,
                data.grade_distribution.D,
                data.grade_distribution.F,
              ]}
              type="donut"
              height={300}
            />
          </div>
        </div>

      </div>

      {/* ── Top Performers — leaderboard ────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <Trophy className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Top Performing Agents</span>
        </div>
        <div className="divide-y divide-border/40">
          {data.top_performers.map((agent, index) => {
            const rank = RANK_CONFIG[index] ?? RANK_CONFIG[2];
            return (
              <div key={agent.agent_name} className="flex items-center gap-4 px-4 py-3">
                {/* Rank badge */}
                <div
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                    rank.cls,
                  )}
                >
                  {rank.label}
                </div>

                {/* Name */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{agent.agent_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {agent.conversations_reviewed} conversations reviewed
                  </p>
                </div>

                {/* Score + bar */}
                <div className="flex flex-col items-end gap-1.5 shrink-0 min-w-[100px]">
                  <span className={cn('text-base font-semibold tabular-nums', scoreColor)}>
                    {agent.avg_score.toFixed(1)}
                    <span className="text-xs font-normal text-muted-foreground ml-0.5">/ 100</span>
                  </span>
                  <Progress value={agent.avg_score} className="h-1.5 w-24" />
                </div>
              </div>
            );
          })}
          {data.top_performers.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No performer data available
            </div>
          )}
        </div>
      </div>

      {/* ── Improvement Priorities ──────────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Improvement Priorities</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Criteria</TableHead>
              <TableHead>Avg Score</TableHead>
              <TableHead className="text-right w-28">Priority</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.improvement_areas.map((area) => {
              const cfg = PRIORITY_CONFIG[area.priority];
              return (
                <TableRow key={area.criteria}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', cfg.dot)} />
                      <span className="text-sm font-medium">{area.criteria}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={area.avg_score} className="h-1.5 w-24" />
                      <span className="text-xs tabular-nums text-muted-foreground w-8 shrink-0">
                        {area.avg_score.toFixed(1)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn(
                      'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium border',
                      cfg.badge,
                    )}>
                      <cfg.Icon className="h-3 w-3" />
                      {area.priority}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
            {data.improvement_areas.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                  No improvement areas identified
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Recent Quality Assessments ──────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Recent Quality Assessments</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16">ID</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Overall Score</TableHead>
              <TableHead className="text-center w-16">Grade</TableHead>
              <TableHead className="text-right">Completeness</TableHead>
              <TableHead className="text-right">Tone</TableHead>
              <TableHead className="text-right">Resolution</TableHead>
              <TableHead className="text-right">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.conversations.slice(0, 10).map((conv) => (
              <TableRow key={conv.conversation_id}>
                <TableCell className="text-xs text-muted-foreground tabular-nums">
                  #{conv.conversation_id}
                </TableCell>
                <TableCell className="text-sm">{conv.contact_name}</TableCell>
                <TableCell className="text-sm">{conv.agent_name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress value={conv.overall_score} className="h-1.5 w-16" />
                    <span className="text-sm tabular-nums font-medium w-8 shrink-0">
                      {conv.overall_score.toFixed(0)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <span className={cn(
                    'inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-bold',
                    GRADE_CLASS[conv.grade],
                  )}>
                    {conv.grade}
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {conv.detailed_scores.completeness.toFixed(0)}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {conv.detailed_scores.tone.toFixed(0)}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                  {conv.detailed_scores.resolution.toFixed(0)}
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                  {conv.date}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ── Scoring Criteria Reference ──────────────────────────────────────── */}
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
          <Info className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">Quality Scoring Criteria</span>
        </div>
        <div className="grid gap-0 divide-y divide-border/40 md:grid-cols-2 md:divide-y-0 lg:grid-cols-3">
          {[
            { label: 'Response Completeness', weight: '25%', desc: 'AI analyzes if all customer questions were answered comprehensively' },
            { label: 'Tone & Professionalism', weight: '20%', desc: 'Sentiment analysis on agent messages for empathy and professionalism' },
            { label: 'Resolution Effectiveness', weight: '25%', desc: 'Correlation with issue resolution and CSAT scores' },
            { label: 'Adherence to Guidelines', weight: '15%', desc: 'Checklist for greeting, empathy, solution, and closing' },
            { label: 'Response Timeliness', weight: '15%', desc: 'Response time performance against SLA targets' },
          ].map(({ label, weight, desc }) => (
            <div key={label} className="flex flex-col gap-1 px-4 py-3 md:border-r md:border-border/40 last:border-r-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{label}</span>
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {weight}
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default ConversationQualityScoring;
